import clsx from '@vangst/lib/clsx'
import { forwardRef } from 'react'

export type RadioType = React.InputHTMLAttributes<
  HTMLInputElement | HTMLLabelElement
> & {
  readonly children: React.ReactNode
  readonly classNameLabel?: string
  readonly label: string | React.ReactNode
  readonly name: string
  readonly reference?: React.Ref<HTMLInputElement> | any
  readonly value: string
}

/**
 * The `li > label > input[type="radio"]` elements.
 *
 * Radio must be a direct descendant of a `ControlGroup` component or a
 * `ul/ol` element.
 *
 * @see [MDN Radio](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/radio)
 *
 * @example
 * <ControlGroup label="Radio Column" name="radio-1" type="radio">
    <RadioOverlay
      classNameLabel="flex items-center"
      name="radio-d"
      label="Yes"
      value="yes"
    >
      <span
        className={clsx(
          'm-auto flex h-14 w-18 items-center justify-center rounded-md border bg-white p-2',
        )}
      >
        <MdThumbUp />
      </span>
    </RadioOverlay>
    <RadioOverlay
      classNameLabel="flex items-center"
      name="radio-d"
      label="No"
      value="no"
    >
      <span
        className={clsx(
          'm-auto flex h-14 w-18 items-center justify-center rounded-md border bg-white p-2',
        )}
      >
        <MdThumbDown />
      </span>
    </RadioOverlay>
 * </ControlGroup>
 *
 */
const RadioOverlay = forwardRef<HTMLInputElement, RadioType>(function Radio(
  props,
  ref,
) {
  const {
    children,
    className,
    classNameLabel,
    label,
    name,
    reference,
    value,
    ...rest
  } = props
  const id = props.id || props.name

  return (
    <li key={`${id}-${value}`} className="list-none">
      <label
        htmlFor={props.id || `${name}-${value}`}
        className={clsx('inline-flex cursor-pointer gap-3', classNameLabel)}
      >
        <input
          className="peer/radioinput hidden"
          id={props.id || `${name}-${value}`}
          name={name}
          ref={ref || reference}
          type="radio"
          value={value}
          {...rest}
        />
        <span
          className={clsx(
            'cursor-pointer text-grey-dark transition peer-checked/radioinput:text-orange peer-hocus/radioinput:text-orange/30',
            className,
          )}
        >
          {children}
        </span>
        <span>{label}</span>
      </label>
    </li>
  )
})

export default RadioOverlay
